// useLiveElapsed.js
import { useEffect, useMemo, useRef, useState } from "react"

/**
 * Live elapsed time calculator.
 *
 * @param startedAt   Firestore Timestamp-like {seconds:number} | null
 * @param baseSec     number of already accumulated seconds (persisted total)
 * @param running     whether the session is currently running
 * @param intervalMs  tick interval (default 1000ms)
 */
export default function useLiveElapsed(startedAt, baseSec = 0, running = true, intervalMs = 1000) {
  const startSec = Number.isFinite(startedAt?.seconds) ? startedAt.seconds : null
  const [now, setNow] = useState(() => Date.now())
  const intervalRef = useRef(null)

  // start/stop ticking
  useEffect(() => {
    if (!running || !startSec) return

    const tick = () => setNow(Date.now())
    const ms = Math.max(250, intervalMs | 0) // clamp + coerce to int
    intervalRef.current = setInterval(tick, ms)

    const onVis = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        tick()
      }
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVis)
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVis)
      }
    }
  }, [running, startSec, intervalMs])

  const liveSec = useMemo(() => {
    if (!startSec) return 0
    const delta = Math.floor(now / 1000) - startSec
    return delta > 0 ? delta : 0
  }, [now, startSec])

  const base = Number.isFinite(baseSec) ? baseSec : 0
  const totalSec = base + (running ? liveSec : 0)

  return {
    seconds: totalSec,
    formatted: formatDuration(totalSec),
  }
}

/** h:mm:ss for <24h, or d h:mm:ss for >=24h */
export function formatDuration(sec) {
  const s = Math.max(0, Math.floor(sec))
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  const pad2 = (n) => String(n).padStart(2, "0")
  return d > 0 ? `${d}d ${h}:${pad2(m)}:${pad2(ss)}` : `${h}:${pad2(m)}:${pad2(ss)}`
}

/** legacy short formatter (m:ss or h:mm:ss) if you still want it */
export const formatHMS = (sec) => {
  const s = Math.max(0, Math.floor(sec))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  const pad2 = (n) => String(n).padStart(2, "0")
  return h > 0 ? `${h}:${pad2(m)}:${pad2(ss)}` : `${m}:${pad2(ss)}`
}
